import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  MessageCircle,
  Send,
  CheckCircle2,
  Mail,
  Phone,
  AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildSchema = (f) =>
  z.object({
    name: z.string().min(1, f.required).max(120),
    email: z.string().min(1, f.required).email(f.invalidEmail),
    phone: z.string().max(40).optional().or(z.literal("")),
    company: z.string().max(160).optional().or(z.literal("")),
    industry: z.string().max(80).optional().or(z.literal("")),
    quantity: z.string().max(40).optional().or(z.literal("")),
    message: z.string().min(1, f.required).max(3000),
    inquiry_type: z.enum(["general", "bulk", "reputation", "personalization"]),
  });

export default function Contact() {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const schema = buildSchema(f);
  const [submitted, setSubmitted] = useState(false);

  const initialType = (() => {
    if (typeof window === "undefined") return "general";
    const v = window.sessionStorage.getItem("in7s_inquiry_type");
    return v || "general";
  })();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      industry: "",
      quantity: "",
      message: "",
      inquiry_type: initialType,
    },
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem("in7s_inquiry_type");
    const v = window.sessionStorage.getItem("in7s_variant");
    if (v) {
      const prefix =
        lang === "de"
          ? `Interesse an Variante: ${v}.\n\n`
          : `Interested in variant: ${v}.\n\n`;
      setValue("message", prefix, { shouldValidate: false });
      window.sessionStorage.removeItem("in7s_variant");
    }
    const typeHandler = (e) => {
      const next = e?.detail;
      if (!next) return;
      setValue("inquiry_type", next, { shouldValidate: true });
    };
    const variantHandler = (e) => {
      const v = e?.detail;
      if (!v) return;
      const prefix =
        lang === "de"
          ? `Interesse an Variante: ${v}.\n\n`
          : `Interested in variant: ${v}.\n\n`;
      setValue("message", prefix, { shouldValidate: false });
    };
    window.addEventListener("in7s:set-inquiry-type", typeHandler);
    window.addEventListener("in7s:set-variant", variantHandler);
    return () => {
      window.removeEventListener("in7s:set-inquiry-type", typeHandler);
      window.removeEventListener("in7s:set-variant", variantHandler);
    };
  }, [setValue, lang]);

  const industryOptions = t.industries.items.map((i) => i.name);
  const typeOptions = [
    { value: "general", label: f.typeGeneral },
    { value: "personalization", label: f.typePersonalization },
    { value: "bulk", label: f.typeBulk },
    { value: "reputation", label: f.typeReputation },
  ];

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        language: lang,
        phone: data.phone || null,
        company: data.company || null,
        industry: data.industry || null,
        quantity: data.quantity || null,
      };
      await axios.post(`${API}/inquiries`, payload, { timeout: 15000 });
      toast.success(f.success);
      setSubmitted(true);
      reset({
        name: "",
        email: "",
        phone: "",
        company: "",
        industry: "",
        quantity: "",
        message: "",
        inquiry_type: data.inquiry_type,
      });
    } catch (err) {
      console.error(err);
      toast.error(f.error);
    }
  };

  const waMessage =
    lang === "de"
      ? "Hallo in7sekunden, ich interessiere mich für einen NFC-Aufsteller."
      : "Hi in7sekunden, I'm interested in an NFC stand.";

  return (
    <section id="contact" className="section-y bg-brand-cloud" data-testid="contact-section">
      <Toaster richColors position="top-center" />
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="overline">{t.contact.overline}</span>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-ink text-balance">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-brand-graphite leading-relaxed text-pretty">
              {t.contact.body}
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={CONTACT.whatsappUrl(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="card-soft card-soft-hover p-5 flex items-center gap-4 group"
                data-testid="contact-whatsapp"
              >
                <span className="grid place-items-center h-11 w-11 rounded-full bg-brand-ink text-white">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-brand-ink">{t.contact.whatsapp}</div>
                  <div className="text-xs text-brand-stone">{CONTACT.whatsappDisplay}</div>
                </div>
                <Send className="h-4 w-4 text-brand-stone group-hover:text-brand-ink transition-colors" />
              </a>

              <a
                href={`mailto:${CONTACT.email}`}
                className="card-soft card-soft-hover p-5 flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <span className="grid place-items-center h-11 w-11 rounded-full bg-brand-ink text-white">
                  <Mail className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-brand-ink">E-Mail</div>
                  <div className="text-xs text-brand-stone">{CONTACT.email}</div>
                </div>
                <Send className="h-4 w-4 text-brand-stone group-hover:text-brand-ink transition-colors" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="card-soft p-6 md:p-8 lg:p-10 space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={f.name} error={errors.name?.message} htmlFor="name">
                  <Input
                    id="name"
                    type="text"
                    autoComplete="name"
                    {...register("name")}
                    className="rounded-xl h-11"
                    data-testid="form-name"
                  />
                </Field>
                <Field label={f.email} error={errors.email?.message} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className="rounded-xl h-11"
                    data-testid="form-email"
                  />
                </Field>
                <Field label={f.phone} htmlFor="phone">
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    {...register("phone")}
                    className="rounded-xl h-11"
                    data-testid="form-phone"
                  />
                </Field>
                <Field label={f.company} htmlFor="company">
                  <Input
                    id="company"
                    type="text"
                    autoComplete="organization"
                    {...register("company")}
                    className="rounded-xl h-11"
                    data-testid="form-company"
                  />
                </Field>
                <Field label={f.industry} htmlFor="industry">
                  <Select
                    value={watch("industry") || ""}
                    onValueChange={(v) => setValue("industry", v, { shouldValidate: true })}
                  >
                    <SelectTrigger
                      id="industry"
                      className="rounded-xl h-11"
                      data-testid="form-industry"
                    >
                      <SelectValue placeholder={f.industryPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {industryOptions.map((i) => (
                        <SelectItem key={i} value={i}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={f.quantity} htmlFor="quantity">
                  <Select
                    value={watch("quantity") || ""}
                    onValueChange={(v) => setValue("quantity", v, { shouldValidate: true })}
                  >
                    <SelectTrigger
                      id="quantity"
                      className="rounded-xl h-11"
                      data-testid="form-quantity"
                    >
                      <SelectValue placeholder={f.quantityPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {t.contact.quantities.map((q) => (
                        <SelectItem key={q} value={q}>
                          {q}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label={f.type} htmlFor="inquiry_type">
                <Select
                  value={watch("inquiry_type")}
                  onValueChange={(v) => setValue("inquiry_type", v, { shouldValidate: true })}
                >
                  <SelectTrigger
                    id="inquiry_type"
                    className="rounded-xl h-11"
                    data-testid="form-type"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={f.message} error={errors.message?.message} htmlFor="message">
                <Textarea
                  id="message"
                  rows={5}
                  {...register("message")}
                  className="rounded-xl resize-y min-h-[120px]"
                  data-testid="form-message"
                />
              </Field>

              <p className="text-xs text-brand-stone leading-relaxed">{f.privacy}</p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="form-submit"
              >
                {isSubmitting ? f.sending : t.contact.send}
                {!isSubmitting && <Send className="h-4 w-4" />}
              </button>

              {submitted && (
                <div
                  className="mt-4 flex items-start gap-3 rounded-xl bg-brand-ink text-white p-4"
                  data-testid="form-success"
                >
                  <CheckCircle2 className="h-5 w-5 text-brand-champagne shrink-0 mt-0.5" />
                  <p className="text-sm">{f.success}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium tracking-wide text-brand-graphite">
        {label}
      </Label>
      {children}
      {error && (
        <div className="flex items-center gap-1.5 text-[11px] text-destructive">
          <AlertCircle className="h-3 w-3" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
